// RC8 · Briefing hero imagery — resolved at read time through the
// canonical hero endpoint (published/approved heroes only; §17-B: no
// cross-subject substitution). Renders nothing when no hero exists.

import React from "react";

const API = process.env.REACT_APP_BACKEND_URL;

export function useHeroImage(subjectType, subjectId) {
  const [hero, setHero] = React.useState(null);
  React.useEffect(() => {
    let live = true;
    setHero(null);
    if (!subjectType || !subjectId) return undefined;
    (async () => {
      try {
        const r = await fetch(
          `${API}/api/media/hero/${encodeURIComponent(subjectType)}/${encodeURIComponent(subjectId)}`,
          { credentials: "include" },
        );
        if (!r.ok) return;
        const j = await r.json();
        if (live && j && j.image_url) {
          const widths = Array.isArray(j.rendition_widths) ? j.rendition_widths : [];
          setHero({
            src: `${API}${j.image_url}`,
            // Sized renditions of the same photograph — the browser picks the one nearest
            // its device pixels instead of downscaling the master 3-4× itself.
            srcSet: widths.length ? widths.map((w) => `${API}${j.image_url}?w=${w} ${w}w`).join(", ") : undefined,
            alt: j.alt || "",
            credit: j.credit || null,
          });
        }
      } catch { /* silent — text-only card */ }
    })();
    return () => { live = false; };
  }, [subjectType, subjectId]);
  return hero;
}

export function BriefingMasthead({ shipId, shipName, compact = false }) {
  const hero = useHeroImage("ship", shipId);
  if (!hero) return null;
  return (
    <figure className={`mt-8 -mx-2 sm:mx-0 ${compact ? "max-w-3xl" : ""}`} data-testid="briefing-masthead">
      <div className={`overflow-hidden rounded-2xl bg-slate-200 aspect-[16/8] ${compact ? "sm:aspect-[16/6]" : "sm:aspect-[16/7]"}`}>
        <img
          src={hero.src}
          srcSet={hero.srcSet}
          sizes={hero.srcSet ? (compact ? "(min-width: 1024px) 768px, 100vw" : "(min-width: 1280px) 720px, (min-width: 768px) 70vw, 100vw") : undefined}
          alt={hero.alt || shipName || ""}
          loading="eager"
          decoding="async"
          className="w-full h-full object-cover"
          data-testid="briefing-masthead-image"
        />
      </div>
      {hero.credit && (
        <figcaption className="mt-2 text-[10.5px] text-slate-400 text-right">{hero.credit}</figcaption>
      )}
    </figure>
  );
}

export function ItemThumbnail({ media, alt }) {
  const hero = useHeroImage(media?.type, media?.id);
  if (!hero) return null;
  return (
    <div
      className="overflow-hidden rounded-xl bg-slate-200 aspect-[16/9] w-full sm:w-44 sm:aspect-[4/3] shrink-0"
      data-testid="briefing-item-thumbnail"
    >
      <img
        src={hero.src}
        alt={hero.alt || alt || ""}
        loading="lazy"
        decoding="async"
        className="w-full h-full object-cover"
      />
    </div>
  );
}
